import { useCallback, useEffect, useState } from 'react';
import { type SearchResultItem } from '@jabberr/core';
import { api } from '../../lib/api.js';
import { useNavigate } from 'react-router';
import { SearchIcon } from '../icons.js';

/** Sidebar search: the box filters the sidebar's own lists as you type (the Sidebar reads `query`), and from two
 *  characters a debounced server search adds matching replies, memory and state documents below. */
export function SidebarSearch({ closeSidebar, query, setQuery }: { closeSidebar: () => void; query: string; setQuery: (q: string) => void }) {
  const navigate = useNavigate();
  const [hits, setHits] = useState<SearchResultItem[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Search across everything the caller can see, debounced; two characters wake it.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits(null);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearching(true);
      api
        .search(q, controller.signal)
        .then(({ results }) => setHits(results))
        .catch(() => undefined)
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const openHit = useCallback(
    (hit: SearchResultItem) => {
      setQuery('');
      setHits(null);
      closeSidebar();
      if (hit.kind === 'memory') navigate(`/p/${hit.projectId}/memory`);
      else if (hit.kind === 'state') navigate(`/p/${hit.projectId}/state`);
      else if (hit.conversationId) {
        const q = hit.turnId ? `?highlight=${hit.turnId}` : '';
        navigate(`/p/${hit.projectId}/c/${hit.conversationId}${q}`);
      } else navigate(`/p/${hit.projectId}`);
    },
    [navigate, closeSidebar, setQuery],
  );

  const hitLabel = (kind: SearchResultItem['kind']): string => {
    switch (kind) {
      case 'project':
        return 'project';
      case 'conversation':
        return 'conversation';
      case 'turn':
        return 'reply';
      case 'memory':
        return 'memory';
      case 'state':
        return 'state document';
    }
  };

  return (
    <>
      <div className="side-search">
        <SearchIcon />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setQuery('');
              setHits(null);
            }
          }}
          placeholder="Find a project or conversation"
          aria-label="Find a project or conversation"
        />
      </div>

      {hits ? (
        <nav className="side-section" aria-live="polite" aria-label="Search results">
          <div className="side-heading">
            <span>More matches</span>
          </div>
          {hits.length === 0 && !searching ? <div className="muted small">No matches.</div> : null}
          {hits.length === 0 && searching ? <div className="muted small">Searching…</div> : null}
          {hits.map((hit, i) => (
            <button key={`${hit.kind}-${hit.turnId ?? hit.conversationId ?? hit.projectId}-${i}`} type="button" className="side-link hit" onClick={() => openHit(hit)}>
              <span className="conv-title">{hit.snippet}</span>
              <span className="muted tiny">
                {hitLabel(hit.kind)} · {hit.projectName}
              </span>
            </button>
          ))}
        </nav>
      ) : null}
    </>
  );
}
